import { Injectable } from '@angular/core';
import { FormsConstants } from '../types/forms-constants';
import { AstoriaXmlUtilities } from '../utilities/astoria-xml-utilities';

const orgDataTagNames: readonly string[] = [
    'COMPANY',
    'GROUP',
    'SW-VER',
    'UI-NO',
    'AUTHOR',
    'PRODUCT',
    'PRODTYPE',
    'MODALITY',
    'C-DATE',
    'PRODUCT-KEY',
    'DOCTYPE-KEY',
    'VARIANTE-KEY',
    'VERSION',
    'LANGUAGE-KEY',
    'REPLACE',
    'LANGUAGE',
    'ORIGLANGUAGE',
    'E-DATE',
    'TRANSLTR',
    'PART-NO',
    'DEPARTMT',
    'VARIANTE',
];

// TODO: Review and improve code in this service
//       - Move private static member functions out to pure functions
//       - Refactor to use helper functions of AstoriaXmlUtilities (eg. getFirstNode, getChildNodes, ...)
@Injectable({
    providedIn: 'root',
})
export class PrologueReplacementService {
    // tags whose value needs to be put into the prologue

    // Tags that should not be translated are documented here:
    // DOCTYPE -> eg. Maintenance Protocol / Wartungsprotokoll

    /**
     * This function is used to replace the prologue of a protocol given as string with another prologue, while
     * extracting and re-inserting data that needs to be transported from the original prologue to the new one.
     * This mechanism enables us to display a unified, translated prologue to the user.
     *
     * To parse and edit the XML it is converted into a native XML DOM in the browser. That XML DOM can then be
     * navigated by using XPath expressions.
     *
     * @param formId the form id
     * @param type the formType of type FormsConstants.FORMTYPE
     * @param xml the xml string
     * @param prologue the xml string of the prologue
     * @returns string processed prologue
     */
    public process(formId: string, type: FormsConstants.FORMTYPE, xml: string, prologue: string): string {
        // create XML DOMs for protocol
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'text/xml');
        const prologueDoc = parser.parseFromString(prologue, 'text/xml');

        // update entity references
        PrologueReplacementService.updateSignatureEntities(xmlDoc, prologueDoc);

        // process FMP tag
        PrologueReplacementService.addInstructionId(prologueDoc, formId, type);
        PrologueReplacementService.updateOrgData(xmlDoc, prologueDoc);
        PrologueReplacementService.updateCopyrightData(xmlDoc, prologueDoc);
        PrologueReplacementService.updateCustomerInformation(xmlDoc, prologueDoc);
        PrologueReplacementService.replaceFmpTag(xmlDoc, prologueDoc); // replacement always has to be the last step in processing

        // process SECTION tag
        PrologueReplacementService.replaceSectionTag(xmlDoc, prologueDoc);

        // remove parser error. The parser throws an error when trying to parse the entity definitions using a
        // backslash as path divider.
        PrologueReplacementService.removeParserError(xmlDoc);

        // set serialized XML string
        const xmlSerializer = new XMLSerializer();
        return xmlSerializer.serializeToString(xmlDoc);
    }

    /**
     * Find entities referenced by signatures in original XML and replace the ones used in the replacement prologue
     * with them.
     *
     * @param xmlDoc the XML Document
     * @param prologueDoc the prologue Document
     * @throws error if not all entities are found
     */
    private static updateSignatureEntities(xmlDoc: Document, prologueDoc: Document): void {
        // find entities of signatures in original prologue and get references to the first two figure tags

        const xmlSignatureFigures = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL/SECTION//SIGNATURE-IMG//FIGURE',
            xmlDoc,
            null,
            XPathResult.ORDERED_NODE_ITERATOR_TYPE,
            null,
        );

        const xmlSignatureFigure0 = <Element>xmlSignatureFigures.iterateNext();
        const xmlSignatureFigure1 = <Element>xmlSignatureFigures.iterateNext();

        // find entities of signatures in replacement prologue and get references to the first two figure tags

        const prologueSignatureFigures = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/SECTION//SIGNATURE-IMG//FIGURE',
            prologueDoc,
            null,
            XPathResult.ORDERED_NODE_ITERATOR_TYPE,
            null,
        );

        const prologueSignatureFigure0 = <Element>prologueSignatureFigures.iterateNext();
        const prologueSignatureFigure1 = <Element>prologueSignatureFigures.iterateNext();

        // update value of attribute "ENTITY" in prologue
        const prologueSignatureFigure0Entity = prologueSignatureFigure0.attributes.getNamedItem('ENTITY');
        const prologueSignatureFigure1Entity = prologueSignatureFigure1.attributes.getNamedItem('ENTITY');
        const xmlSignatureFigure0Entity = xmlSignatureFigure0.attributes.getNamedItem('ENTITY');
        const xmlSignatureFigure1Entity = xmlSignatureFigure1.attributes.getNamedItem('ENTITY');

        if (
            !prologueSignatureFigure0Entity ||
            !prologueSignatureFigure1Entity ||
            !xmlSignatureFigure0Entity ||
            !xmlSignatureFigure1Entity
        ) {
            throw new Error(
                `PrologueReplacementService: updateSignatureEntities: Could not find all entities necessary to update the signature image entities!`,
            );
        }

        prologueSignatureFigure0Entity.value = xmlSignatureFigure0Entity.value;
        prologueSignatureFigure1Entity.value = xmlSignatureFigure1Entity.value;
    }

    /**
     * Deduce name of instructions used along this protocol and insert it into CHSHEET tag inside of FMP tag
     *
     * @param prologueDoc prologue document
     * @param formId form id
     * @param formType form type of type FormsConstants.FORMTYPE
     * @throws error if no instruction node could be found
     */
    private static addInstructionId(prologueDoc: Document, formId: string, formType: FormsConstants.FORMTYPE): void {
        // generate instruction name and fill it in new prologue
        // example: 'C2-029.832.02.09.02' -> 'C2-029.831.02.09.XX'
        const instructionType = parseInt(formType, 10) - 1;
        const instructionEndingTrimAmount = 2;
        let instructionId = formId.replace(formType, instructionType.toString());
        instructionId = instructionId.substring(0, instructionId.length - instructionEndingTrimAmount) + 'XX';

        // find and adapt instruction text in new prologue

        const instructionNode = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP/CHSHEET/ADD-USE[position()=2]',
            prologueDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;

        // two examples for the string we are deconstructing using regex here:
        // - "The instructions TD00-831.831.01.01.XX are required for this protocol"
        // - "Zu diesem Protokoll gehört die Anleitung TD00-831.831.01.01.XX"
        const instructionRegex = new RegExp(
            '([a-zA-Z0-9öäüÖÄÜß. ]+ )[a-zA-Z]+[0-9]*-[0-9._]+XX($| [a-zA-Z0-9öäüÖÄÜß ]*)',
        );

        if (!instructionNode) {
            throw new Error(`PrologueReplacementService: addInstructionId: Could not find instruction node!`);
        }

        const instructionText = instructionNode.textContent || '';
        instructionNode.textContent = instructionText.replace(instructionRegex, '$1' + instructionId + '$2');
    }

    /**
     * Extract org data from original protocol to be inserted into new prologue before replacement is done
     *
     * @param xmlDoc xml Document
     * @param prologueDoc prologue Document
     */
    private static updateOrgData(xmlDoc: Document, prologueDoc: Document): void {
        // extract existing data from protocol
        const orgDataTags = orgDataTagNames.map((tagName) => {
            const node = xmlDoc.evaluate(
                '/MED-DOC/PROTOCOL/FMP/ORGDATA/' + tagName,
                xmlDoc,

                null,
                XPathResult.FIRST_ORDERED_NODE_TYPE,

                null,
            ).singleNodeValue;

            return {
                name: tagName,
                content: node ? node.textContent : '',
            };
        });

        // fill out info in prologue
        orgDataTags.forEach((orgDataTag) => {
            const newOrgDataTag = prologueDoc.evaluate(
                '/MED-DOC/PROTOCOL/FMP/ORGDATA/' + orgDataTag.name,
                prologueDoc,
                null,
                XPathResult.FIRST_ORDERED_NODE_TYPE,
                null,
            ).singleNodeValue;

            if (newOrgDataTag) {
                newOrgDataTag.textContent = orgDataTag.content;
            }
        });
    }

    /**
     * Copy the copyright claim over
     *
     * @param xmlDoc xml Document
     * @param prologueDoc prologue Document
     * @throws error if no copyright or footer node could be found
     */
    private static updateCopyrightData(xmlDoc: Document, prologueDoc: Document): void {
        const copyrightLine1Node = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP/FMP-CONTENT/COPYRIGHT/C-LINE1',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const copyrightLine1 = copyrightLine1Node ? copyrightLine1Node.textContent : '';
        const footerNode = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP/FMP-CONTENT/FOOTER/LINE1/INNER',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const footer = footerNode ? footerNode.textContent : '';

        const newCopyrightLine1TagNode = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP/FMP-CONTENT/COPYRIGHT/C-LINE1',
            prologueDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        if (!newCopyrightLine1TagNode) {
            throw new Error(`PrologueReplacementService: updateCopyrightData: could not find new copyright node!`);
        }
        newCopyrightLine1TagNode.textContent = copyrightLine1;

        const footerTagNode = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP/FMP-CONTENT/FOOTER/LINE1/INNER',
            prologueDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        if (!footerTagNode) {
            throw new Error(`PrologueReplacementService: updateCopyrightData: could not find footer node!`);
        }

        footerTagNode.textContent = footer;
    }

    /**
     * Copy the customer information over
     *
     * @param xmlDoc xml Document
     * @param prologueDoc prologue Document
     */
    private static updateCustomerInformation(xmlDoc: Document, prologueDoc: Document): void {
        const customerInformation = AstoriaXmlUtilities.getCustomerIdentification(xmlDoc);

        Object.values(customerInformation).forEach((i) =>
            AstoriaXmlUtilities.setPdfFormByXmlId(prologueDoc, i.xmlId, i.value),
        );
    }

    /**
     * Replace the FMP tag in the original protocol with the updated FMP tag from the prologue
     *
     * @param xmlDoc xml Document
     * @param prologueDoc prologue Document
     * @throws error if protocol, old fmp or new fmp nodes could not be found
     */
    private static replaceFmpTag(xmlDoc: Document, prologueDoc: Document): void {
        const protocolNode = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const oldFmpNode = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const newFmpNode = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/FMP',
            prologueDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;

        if (!protocolNode) {
            throw new Error(`PrologueReplacementService: replaceFmpTag: Could not find protocol node!`);
        }
        if (!oldFmpNode) {
            throw new Error(`PrologueReplacementService: replaceFmpTag: Could not find old fmp node!`);
        }
        if (!newFmpNode) {
            throw new Error(`PrologueReplacementService: replaceFmpTag: Could not find new fmp node!`);
        }

        protocolNode.replaceChild(newFmpNode, oldFmpNode);
    }

    /**
     * Replace the SECTION tag in the original protocol with the updated SECTION tag from the prologue
     *
     * @param xmlDoc xml document
     * @param prologueDoc prologue document
     * @throws error if protocol, old section page or new section page nodes cannot be found
     */
    private static replaceSectionTag(xmlDoc: Document, prologueDoc: Document): void {
        const protocolNode = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const oldSectionPageNode = xmlDoc.evaluate(
            '/MED-DOC/PROTOCOL/SECTION[position()=1]',
            xmlDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;
        const newSectionPageNode = prologueDoc.evaluate(
            '/MED-DOC/PROTOCOL/SECTION[position()=1]',
            prologueDoc,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;

        if (!protocolNode) {
            throw new Error(`PrologueReplacementService: replaceSectionTag: Could not find protocol node!`);
        }
        if (!newSectionPageNode) {
            throw new Error(`PrologueReplacementService: replaceSectionTag: Could not find new section page node!`);
        }
        if (!oldSectionPageNode) {
            throw new Error(`PrologueReplacementService: replaceSectionTag: Could not find old section page node!`);
        }

        protocolNode.replaceChild(newSectionPageNode, oldSectionPageNode);
    }

    /**
     * Remove parsererror tags from XML that might have been created as reaction to non-understood path dividers in
     * entity definitions:
     * <parsererror xmlns="http://www.w3.org/1999/xhtml" style="display: block; white-space: pre; border: 2px solid #c77; padding: 0 1em 0 1em; margin: 1em; background-color: #fdd; color: black">
     *     <h3>This page contains the following errors:</h3>
     *     <div style="font-family:monospace;font-size:12px">error on line 1 at column 223: Invalid URI: NewExtObjects\\TD\\TD-COM-EDITION\\TD-SYMBOLS-02\\COM-SYMBOLS\\Z00010ZF-110107-00-14.EPS
     *     </div>
     *     <h3>Below is a rendering of the page up to the first error.</h3>
     * </parsererror>
     * <parsererror xmlns="http://www.w3.org/1999/xhtml" style="display: block; white-space: pre; border: 2px solid #c77; padding: 0 1em 0 1em; margin: 1em; background-color: #fdd; color: black">
     *     <h3>This page contains the following errors:</h3>
     *     <div style="font-family:monospace;font-size:12px">error on line 2 at column 168: Invalid URI: NewExtObjects\\TD\\TD-COM-EDITION\\TD-SYMBOLS-02\\COM-SYMBOLS\\Z00010ZF-110107-00-14.EPS
     *     </div>
     *     <h3>Below is a rendering of the page up to the first error.</h3>
     * </parsererror>
     *
     * @param xmlDoc xml document
     * @private
     */
    private static removeParserError(xmlDoc: Document): void {
        const parserErrorCollection = xmlDoc.getElementsByTagName('parsererror');

        for (let i = parserErrorCollection.length - 1; i >= 0; i--) {
            xmlDoc.documentElement.removeChild(parserErrorCollection[i]);
        }
    }
}
